import { getNextIndex } from './next-index'
import type { PlayerState } from './types'

export type AdvanceOutcome =
  | { type: 'stop'; state: PlayerState }
  | { type: 'pending-radio'; state: PlayerState }
  | { type: 'advance'; state: PlayerState; index: number }

/**
 * The single state transition for "move to the next track".
 *
 * Advancement happens on four separate paths — the `ended` event, the crossfade
 * timer, the manual skip button, and the radio auto-advance — and each one used
 * to re-implement this decision inline. They drifted: radio mode was added to
 * some and not others, which is how a radio queue ended up replaying manual
 * tracks. Anything that moves the playhead forward goes through here.
 *
 * Pure. Callers own their own side effects (channel swaps, refs, media session)
 * and key them off the returned `type`, because those genuinely differ between
 * a gapless swap and a button press.
 */
export function advanceQueue(state: PlayerState): AdvanceOutcome {
  if (state.queue.length === 0) return { type: 'stop', state }

  const result = getNextIndex(state)

  if (result.type === 'stop') {
    return { type: 'stop', state: { ...state, isPlaying: false } }
  }

  if (result.type === 'pending-radio') {
    // Radio has nothing buffered yet. Drop everything after the current track so
    // the queue does not visibly hold stale entries while the fetch is in
    // flight; the caller flags itself to resume once tracks arrive.
    return {
      type: 'pending-radio',
      state: { ...state, queue: state.queue.slice(0, state.currentIndex + 1) },
    }
  }

  return { type: 'advance', state: { ...state, currentIndex: result.index }, index: result.index }
}
